import axios from 'axios';

/**
 * Extrai uma mensagem amigável de um erro qualquer (axios, Error, unknown).
 *
 * Prioridade:
 * 1. Mensagem do backend (`response.data.message`) — nossos endpoints já
 *    retornam textos amigáveis via ValidationPipe/exceções do Nest.
 * 2. Mapeamento por status HTTP (500, 502/503/504, 401, 403, 404).
 * 3. Falha de rede (sem response, ERR_NETWORK).
 * 4. Fallback fornecido pelo caller.
 *
 * Nunca devolve "Request failed with status code XXX" (mensagem crua do
 * axios) — esse texto vazio de significado é justamente o que este helper
 * substitui.
 */
export function getFriendlyErrorMessage(err: unknown, fallback = 'Ocorreu um erro. Tente novamente.'): string {
  if (axios.isAxiosError(err)) {
    // Backend nosso: message pode vir como string ou array (class-validator).
    const backendMsg = (err.response?.data as { message?: string | string[] } | undefined)?.message;
    if (Array.isArray(backendMsg) && backendMsg.length > 0) return backendMsg.join('. ');
    if (typeof backendMsg === 'string' && backendMsg.trim()) return backendMsg;

    // Sem response = rede caiu antes de chegar no servidor.
    if (!err.response || err.code === 'ERR_NETWORK') {
      return 'Sem conexão com o servidor. Verifique sua conexão e tente novamente.';
    }

    const status = err.response.status;
    if (status >= 500 && status <= 504) {
      return status === 500
        ? 'Erro no servidor. Tente novamente em instantes.'
        : 'Serviço temporariamente indisponível. Tente novamente em instantes.';
    }
    if (status === 401) return 'Sessão expirada. Faça login novamente.';
    if (status === 403) return 'Você não tem permissão para essa ação.';
    if (status === 404) return 'Recurso não encontrado.';
    if (status === 408 || err.code === 'ECONNABORTED') {
      return 'A requisição demorou demais. Tente novamente.';
    }
  }

  if (err instanceof Error && err.message && !/^Request failed with status/i.test(err.message)) {
    return err.message;
  }

  return fallback;
}
